import { Employee, RolesEnum, UserSettingsInterface } from "@finway-group/shared/lib/models"
import axios from "axios"

import { FINWAY_ADMIN_EMAIL } from "Shared/config/consts"
import store from "Shared/store"
import { LocalStorageService, UserService } from "."
import { COMPANY_ID } from "./localstorage.service"

const MODEL = "users"

export const mapEmployeeActiveCompany = (employeeObject: Employee) => {
    const companyId = LocalStorageService.get(COMPANY_ID)
    if (companyId) {
        employeeObject.activeCompanyProfile = employeeObject.getUserCompanyProfile(companyId)!
    }
    return employeeObject
}

const EmployeeService = {
    getEmployees: (excludeDeleted: boolean = false, filtered: boolean = false): Employee[] => {
        let employees = store
            .getState()
            .employees.items.filter((employee: any) => employee.email !== FINWAY_ADMIN_EMAIL)
            .map((employee: any) => mapEmployeeActiveCompany(new Employee(employee)))

        let loggedInProfile = UserService.getLoggedInEmployeeProfile()
        if (loggedInProfile) loggedInProfile = mapEmployeeActiveCompany(loggedInProfile)

        if (excludeDeleted) {
            employees = employees?.filter((employee: Employee) => !employee.activeCompanyProfile?.deleted)
        }

        if (filtered) {
            employees = employees?.filter(
                (employee: Employee) =>
                    loggedInProfile.activeCompanyProfile?.isAdmin ||
                    (loggedInProfile.activeCompanyProfile?.isApprover && employee.activeCompanyProfile?.team === loggedInProfile.activeCompanyProfile?.team)
            )
        }

        return employees.map((employee: any) => new Employee(employee))
    },
    getEmployeeIds: (excludeDeleted: boolean = false, filtered: boolean = false) => {
        return EmployeeService.getEmployees(excludeDeleted, filtered).map((employee: Employee) => employee.id)
    },
    getEmployeeById: (id: string) => {
        const employeeInStore = store.getState().employees.items.find((e: Employee) => e.id === id)
        return new Employee(employeeInStore)
    },
    getAutoApprover: () => {
        return store.getState().employees.items.find((e: Employee) => e.email === FINWAY_ADMIN_EMAIL)
    },
    getLoggedInEmployeeProfile: () => {
        return EmployeeService.getEmployeeById(store.getState().auth.user?.id)
    },
    fetchAllEmployees: async (limit: number = 2000) => {
        const {
            data: { docs },
        } = await axios.get(`/${MODEL}?limit=${limit}`)
        return docs.map((item: any) => mapEmployeeActiveCompany(new Employee(item)))
    },
    fetchEmployeeById: async (id: string) => {
        const { data } = await axios.get(`/${MODEL}/${id}`)
        return data ? mapEmployeeActiveCompany(new Employee(data)) : null
    },
    createEmployee: async (createEmployee: Employee) => {
        const { data } = await axios.post(`/${MODEL}`, createEmployee)
        return mapEmployeeActiveCompany(new Employee(data))
    },
    updateEmployee: async (id: string, updateEmployee: Employee) => {
        const { data } = await axios.put(`/users/${id}`, updateEmployee)
        return mapEmployeeActiveCompany(new Employee(data))
    },
    deleteEmployee: async (id: string) => {
        const { data } = await axios.delete(`/${MODEL}/${id}`)
        return mapEmployeeActiveCompany(new Employee(data))
    },
    updateEmployeeSettings: async (id: string, settings: UserSettingsInterface) => {
        const { data } = await axios.put(`/${MODEL}/${id}/settings`, settings)
        return mapEmployeeActiveCompany(new Employee(data))
    },
    updateEmployeeImg: async (id: string, img: string) => {
        const { data } = await axios.put(`/${MODEL}/${id}/img`, { img })
        return mapEmployeeActiveCompany(new Employee(data))
    },
    setCompanyEmailAlias: async (id: string, companyId: string, emailAlias: string) => {
        const { data } = await axios.put(`/${MODEL}/${id}/email-alias`, { companyId, emailAlias })
        return mapEmployeeActiveCompany(new Employee(data))
    },
    filterEmployeesForUser: (employees: Employee[], user: Employee) =>
        employees.filter(({ id, team }: Employee) => {
            switch (user.activeCompanyProfile.role) {
                case RolesEnum.ADMIN:
                    return true
                case RolesEnum.APPROVER:
                    return user.activeCompanyProfile.team === team
                case RolesEnum.EMPLOYEE:
                    return id === user.id
            }
        }),
    requestPhoneNumberVerification: async (phoneNumber: string | undefined) => {
        await axios.post(`/${MODEL}/phone/verification`, { phoneNumber })
    },
    checkPhoneNumberVerification: async (verificationCode: string) => {
        const { data } = await axios.post(`/${MODEL}/phone/verification-check`, { verificationCode })
        return data.verified
    },
    getOneByEmail: async (email: string) => {
        const { data } = await axios.get(`/${MODEL}/email/${email}`)
        return mapEmployeeActiveCompany(new Employee(data))
    },
    inviteUserToSubCompany: async (invitedEmployee: any) => {
        const { data } = await axios.post(`/${MODEL}/invite/${invitedEmployee.email}`, invitedEmployee)
        return mapEmployeeActiveCompany(new Employee(data))
    },
    fetchEmployees: async (queryParams: any, source: any, timeout?: number) => {
        const { data } = await axios.get(
            `/${MODEL}?page=${queryParams.page}&limit=${queryParams.limit}${queryParams.filterQueryString}${queryParams.searchQueryString}${queryParams.sortQueryString}`,
            {
                cancelToken: source.token,
                timeout: timeout,
            }
        )
        return data
    },
    updateGetMyInvoicesUserId: async (id: string, getMyInvoicesUserId: string) => {
        const { data } = await axios.put(`/${MODEL}/${id}/settings/get-my-invoices`, { getMyInvoicesUserId })
        return data
    },
    updateAbsence: async (id: string, absence: any) => {
        const { data } = await axios.put(`/${MODEL}/${id}/absence`, absence)
        return mapEmployeeActiveCompany(new Employee(data))
    },
    updateEmployeeRule: async (id: string, rule: any) => {
        const { data } = await axios.put(`${MODEL}/${id}/rule`, rule)
        return mapEmployeeActiveCompany(new Employee(data))
    },
    getLatestEmployeeCreditorNumber: async (): Promise<number> => {
        const {
            data: { employeeCreditorNumberSequence },
        } = await axios.get(`${MODEL}/sequence/latest-creditor-number`)
        return employeeCreditorNumberSequence
    },
}

export default EmployeeService
